import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  inject,
  OnInit,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  ElementRef,
  DestroyRef,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DifficultyPipe } from '../../../pipes/difficulty.pipe';
import { MessageService, ConfirmationService } from 'primeng/api';
import { TicketService } from '../../../core/services/ticket.service';
import { TicketDetail, TicketTask } from '../../../core/models/ticket.model';
import {
  findActiveTaskLog,
  getCurrentUserId,
  isTaskTimeEntryBlocked,
} from '../../../core/utils/active-task-log.util';
import { TcDataService } from '../../../../../shared/services/ticket-center/tc-data.service';
import { constants } from '../../../../../shared/utils/constants';
import { MarkdownModule } from 'ngx-markdown';
import { FavoriteSelectComponent } from '../../shared/favorite-input';
import {
  EntityPickerComponent,
  EntityPickerSearchParams,
  EntityPickerResult,
} from '../../entity-picker';
import { FavoriteListKey } from '../../../core/models/favorite-list.model';
import { FavoriteOptionsService } from '../../../core/services/favorite-options.service';

interface TaskWithProgress extends TicketTask {
  progress: number;
}

@Component({
  selector: 'app-ticket-tasks',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    DatePickerModule,
    ConfirmDialogModule,
    TooltipModule,
    DialogModule,
    ToggleSwitchModule,
    SelectButtonModule,
    DifficultyPipe,
    MarkdownModule,
    EntityPickerComponent,
    FavoriteSelectComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './ticket-tasks.component.html',
  styleUrls: ['./ticket-tasks.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketTasksComponent implements OnInit, OnChanges {
  /** Enum das listas de favoritos, para uso no template. */
  protected readonly FavoriteKey = FavoriteListKey;

  private readonly ticketService = inject(TicketService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(TcDataService);
  private readonly favoriteOptions = inject(FavoriteOptionsService);

  @Input({ required: true }) ticket!: TicketDetail;
  @Input() canEdit = true;
  /** Tarefa a destacar ao abrir (vinda da sprint): rola até ela e a faz piscar uma vez. */
  @Input() focusTaskId: string | null = null;
  private focusConsumed = false;

  @Output() tasksUpdated = new EventEmitter<TicketTask[]>();
  @Output() startTimeEntry = new EventEmitter<string>();
  /** Emitido quando uma operação que pode alterar dados do chamado é concluída.
   *  'status' → apenas status da tarefa mudou, o chamado pode ser atualizado parcialmente.
   *  'full'   → mudanças pesadas (esforço, usuário, data, criação, exclusão, bulk) — reload completo.
   */
  @Output() ticketRefreshRequired = new EventEmitter<'status' | 'full'>();

  tasks = signal<TaskWithProgress[]>([]);
  showForm = signal(false);
  editingTask = signal<TicketTask | null>(null);
  saving = signal(false);
  draggedIndex = signal<number | null>(null);
  dropTarget = signal<{ index: number; position: 'top' | 'bottom' } | null>(null);

  formSummary = signal('');
  formDescription = signal('');
  formEstimatedEffort = signal<number | null>(null);
  formExpectedDate = signal<Date | null>(null);
  formDifficulty = signal('M');
  formStatusId = signal<string | null>(null);
  formUserId = signal<string | null>(null);
  formChangeExpectedDate = signal(true);
  formFinished = signal(false);
  showBulkUpdate = signal(false);
  bulkStatusId = signal<string | null>(null);
  bulkUserId = signal<string | null>(null);
  bulkExpectedDate = signal<Date | null>(null);
  bulkRemoveExpectedDate = signal(false);
  /** Propaga a nova data de previsão também para o chamado. Desligado por padrão. */
  bulkApplyDateToTicket = signal(false);
  bulkFinish = signal<boolean | null>(null);
  readonly bulkFinishOptions = [
    { label: 'Não concluída', value: false },
    { label: 'Concluir', value: true },
  ];
  selectedTaskIds = signal<Set<string>>(new Set<string>());

  selectedCount = computed(() => this.selectedTaskIds().size);

  allTasksSelected = computed(
    () => this.tasks().length > 0 && this.selectedTaskIds().size === this.tasks().length
  );

  someTasksSelected = computed(
    () => this.selectedTaskIds().size > 0 && !this.allTasksSelected()
  );

  statusesList = signal<any[]>([]);
  statusesLoading = signal(false);

  usersList = signal<any[]>([]);
  usersLoading = signal(false);

  formSystem = signal<any | null>(null);

  readonly searchSystemsFn = (params: EntityPickerSearchParams): Promise<EntityPickerResult> =>
    firstValueFrom(this.ticketService.searchSystems(params.query || '')).then(items => ({
      items,
      total: items.length,
    }));

  readonly difficultyOptions = constants.ticketcenter.difficulties;

  totalPoints = computed(() => {
    return this.tasks().reduce((sum, t) => sum + (t.estimated_effort ?? 0), 0);
  });

  finishedPoints = computed(() => {
    return this.tasks()
      .filter(t => !!t.finished)
      .reduce((sum, t) => sum + (t.estimated_effort ?? 0), 0);
  });

  overallProgress = computed(() => {
    const total = this.totalPoints();
    if (total === 0) return 0;
    return Math.min(100, (this.finishedPoints() / total) * 100);
  });

  formTitle = computed(() => {
    return this.editingTask() ? 'Editar Tarefa' : 'Nova Tarefa';
  });

  ngOnInit(): void {
    this.loadTasksWithProgress();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ticket'] && !changes['ticket'].isFirstChange()) {
      this.updateTasksFromTicket();
      this.refreshTaskProgress();
    }
  }

  private updateTasksFromTicket(): void {
    if (!this.ticket?.tasks) return;
    const sortedTasks = [...this.ticket.tasks].sort((a, b) => a.sequence - b.sequence);
    this.tasks.update(currentTasks =>
      sortedTasks.map(t => {
        const existing = currentTasks.find(et => et.id === t.id);
        return {
          ...t,
          finished: !!t.finished,
          progress: existing ? existing.progress : 0,
        };
      })
    );
  }

  private refreshTaskProgress(): void {
    if (!this.ticket?.id) return;
    this.ticketService.getTasksProgress(this.ticket.id).subscribe({
      next: progressData => {
        const updatedWithProgress = this.tasks().map((task, index) => ({
          ...task,
          progress: Array.isArray(progressData)
            ? (progressData[index]?.percentage ?? 0)
            : (progressData[task.id]?.percentage ?? 0),
        }));
        this.tasks.set(updatedWithProgress);
        this.cdr.markForCheck();
      },
      error: (err: unknown) => console.error('Error loading task progress:', err),
    });
  }

  /** Consumo único: refreshes/socket não devem rolar de novo. */
  private focusTaskIfRequested(): void {
    if (!this.focusTaskId || this.focusConsumed) return;
    this.focusConsumed = true;
    const selector = `[data-task-id="${CSS.escape(this.focusTaskId)}"]`;
    // O painel/accordion pode ainda estar animando ao abrir; tenta até o card existir.
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    this.destroyRef.onDestroy(() => clearTimeout(timer));
    const tryFocus = () => {
      const card = this.host.nativeElement.querySelector<HTMLElement>(selector);
      if (!card) {
        if (++attempts < 30) timer = setTimeout(tryFocus, 100);
        return;
      }
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      card.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      card.classList.add('task-card--flash');
      card.addEventListener('animationend', () => card.classList.remove('task-card--flash'), {
        once: true,
      });
    };
    timer = setTimeout(tryFocus, 150);
  }

  private loadTasksWithProgress(): void {
    this.ticketService.listTasks(this.ticket.id).subscribe({
      next: (tasks: TicketTask[]) => {
        const sortedTasks = [...tasks].sort((a, b) => a.sequence - b.sequence);
        const tasksWithProgress: TaskWithProgress[] = sortedTasks.map(t => ({
          ...t,
          finished: !!t.finished,
          progress: 0,
        }));
        this.tasks.set(tasksWithProgress);
        this.cdr.markForCheck();
        this.focusTaskIfRequested();

        this.ticketService.getTasksProgress(this.ticket.id).subscribe({
          next: progressData => {
            const updatedWithProgress = this.tasks().map((task, index) => ({
              ...task,
              progress: Array.isArray(progressData)
                ? (progressData[index]?.percentage ?? 0)
                : (progressData[task.id]?.percentage ?? 0),
            }));
            this.tasks.set(updatedWithProgress);
            this.cdr.markForCheck();
          },
          error: (err: unknown) => console.error('Error loading task progress:', err),
        });
      },
      error: (err: unknown) => {
        console.error('Error loading tasks:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar as tarefas.',
        });
      },
    });
  }

  openNewTaskForm(): void {
    if (!this.canEdit) return;
    this.resetForm();
    this.editingTask.set(null);

    // Seed users with ticket developer or responsible
    if (this.ticket?.developer?.id) {
      this.formUserId.set(this.ticket.developer.id);
      this.usersList.set([this.ticket.developer]);
    } else if (this.ticket?.responsible?.id) {
      this.formUserId.set(this.ticket.responsible.id);
      this.usersList.set([this.ticket.responsible]);
    } else {
      this.usersList.set([]);
    }

    // Seed statuses: sem tarefas no chamado, a primeira nasce em "Esforço Definido";
    // a partir daí herda o status atual do chamado.
    const seedStatus =
      (this.tasks()?.length ?? 0) === 0
        ? constants.ticketcenter.defaultFirstTaskStatus
        : this.ticket?.status?.id
          ? this.ticket.status
          : null;

    if (seedStatus) {
      this.formStatusId.set(seedStatus.id);
      this.statusesList.set([seedStatus]);
    } else {
      this.statusesList.set([]);
    }

    this.formSystem.set(this.ticket?.system ?? null);

    this.loadInitialOptions();

    this.showForm.set(true);
  }

  openEditTaskForm(task: TicketTask): void {
    if (!this.canEdit) return;
    this.editingTask.set(task);
    this.formSummary.set(task.summary || '');
    this.formDescription.set(task.description || '');
    this.formEstimatedEffort.set(task.estimated_effort);
    this.formExpectedDate.set(task.expected_date ? new Date(task.expected_date) : null);
    this.formDifficulty.set(task.difficulty || 'M');
    this.formStatusId.set(task.status?.id || null);
    this.formUserId.set(task.user?.id || null);
    this.formFinished.set(!!task.finished);

    if (task.user) {
      this.usersList.set([task.user]);
    }

    if (task.status) {
      this.statusesList.set([task.status]);
    }

    this.formSystem.set(task.systems?.[0] ?? this.ticket?.system ?? null);

    this.loadInitialOptions();

    this.showForm.set(true);
  }

  closeForm(): void {
    this.showForm.set(false);
    this.editingTask.set(null);
    this.resetForm();
  }

  private resetForm(): void {
    this.formSummary.set('');
    this.formDescription.set('');
    this.formEstimatedEffort.set(1);
    this.formExpectedDate.set(null);
    this.formDifficulty.set('M');
    this.formStatusId.set(null);
    this.formUserId.set(null);
    this.formSystem.set(null);
    this.formChangeExpectedDate.set(true);
    this.formFinished.set(false);
  }

  /**
   * Buscas iniciais dos selects do formulário. São puladas quando a seleção vai
   * abrir só com os favoritos — quem resolve a listagem nesse caso é o próprio
   * componente de seleção.
   */
  private loadInitialOptions(): void {
    if (!this.favoriteOptions.hasFavorites(FavoriteListKey.TicketDevelopers)) {
      this.loadUsers();
    }
    if (!this.favoriteOptions.hasFavorites(FavoriteListKey.TicketStatuses)) {
      this.loadStatuses();
    }
  }

  loadStatuses(query: string = ''): void {
    this.statusesLoading.set(true);
    this.ticketService.searchTicketStatuses(query).subscribe({
      next: statuses => {
        const currentId = this.formStatusId();
        if (currentId && !statuses.some((s: any) => s.id === currentId)) {
          const currentStatus = this.statusesList().find(s => s.id === currentId);
          if (currentStatus) {
            statuses.unshift(currentStatus);
          }
        }
        this.statusesList.set(statuses);
        this.statusesLoading.set(false);
      },
      error: () => this.statusesLoading.set(false),
    });
  }

  loadUsers(query: string = ''): void {
    this.usersLoading.set(true);
    this.ticketService.searchUsers(query).subscribe({
      next: users => {
        const currentId = this.formUserId();
        if (currentId && !users.some((u: any) => u.id === currentId)) {
          const currentUser = this.usersList().find(u => u.id === currentId);
          if (currentUser) {
            users.unshift(currentUser);
          }
        }
        this.usersList.set(users);
        this.usersLoading.set(false);
      },
      error: () => this.usersLoading.set(false),
    });
  }

  onStatusFilter(event: any): void {
    this.loadStatuses(event.filter || '');
  }

  onUserFilter(event: any): void {
    this.loadUsers(event.filter || '');
  }

  saveTask(): void {
    const summary = this.formSummary().trim();
    const description = this.formDescription().trim();
    const statusId = this.formStatusId();
    const userId = this.formUserId();
    const effort = this.formEstimatedEffort();

    if (!summary || !statusId || !userId || effort === null || effort === undefined) {
      const missing: string[] = [];
      if (!summary) missing.push('descrição resumida');
      if (!statusId) missing.push('status');
      if (!userId) missing.push('desenvolvedor');
      if (effort === null || effort === undefined) missing.push('esforço técnico');
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: `Campo(s) obrigatório(s): ${missing.join(', ')}`,
        life: 3000,
      });
      return;
    }

    if (this.formFinished() && this.hasOpenTimeEntry()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Não é possível concluir a tarefa enquanto houver apontamento em aberto.',
        life: 4000,
      });
      return;
    }

    this.saving.set(true);

    const payload: Partial<TicketTask> & {
      ticket_status_id?: string | null;
      user_id?: string | null;
      systems?: { id: string }[];
      change_ticket_expected_date?: boolean;
    } = {
      summary,
      description,
      estimated_effort: this.formEstimatedEffort(),
      expected_date: this.formExpectedDate()
        ? this.toLocalISOString(this.formExpectedDate() as Date)
        : null,
      difficulty: this.formDifficulty(),
      finished: this.formFinished(),
      ticket_status_id: statusId,
      user_id: userId,
      systems: this.formSystem()?.id ? [{ id: this.formSystem()!.id }] : [],
      change_ticket_expected_date: this.formChangeExpectedDate(),
    };

    const editing = this.editingTask();
    if (editing) {
      this.ticketService
        .updateTask(
          this.ticket.id,
          editing.id,
          payload,
          this.dataService.shouldRecalculateTaskEffort
        )
        .subscribe({
          next: updatedTask => {
            this.tasks.update(currentTasks => {
              const index = currentTasks.findIndex(t => t.id === updatedTask.id);
              if (index > -1) {
                const newTasks = [...currentTasks];
                newTasks[index] = {
                  ...newTasks[index],
                  ...updatedTask,
                  summary: updatedTask.summary || '',
                };
                return newTasks;
              }
              return currentTasks;
            });
            this.saving.set(false);
            this.closeForm();
            this.messageService.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: 'Tarefa atualizada',
            });
            this.tasksUpdated.emit(this.tasks());
            this.ticketRefreshRequired.emit('status');
          },
          error: () => {
            this.saving.set(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: 'Erro ao atualizar tarefa',
              life: 5000,
            });
          },
        });
    } else {
      payload.sequence = (this.tasks()?.length ?? 0) + 1;
      this.ticketService
        .createTask(this.ticket.id, payload, this.dataService.shouldRecalculateTaskEffort)
        .subscribe({
          next: newTask => {
            this.tasks.update(currentTasks => [...currentTasks, { ...newTask, progress: 0 }]);
            this.saving.set(false);
            this.closeForm();
            this.messageService.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: 'Tarefa criada',
            });
            this.tasksUpdated.emit(this.tasks());
            this.ticketRefreshRequired.emit('status');
          },
          error: () => {
            this.saving.set(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: 'Erro ao criar tarefa',
              life: 5000,
            });
          },
        });
    }
  }

  openBulkUpdate(): void {
    if (!this.canEdit) return;
    this.bulkStatusId.set(null);
    this.bulkUserId.set(null);
    this.bulkExpectedDate.set(null);
    this.bulkRemoveExpectedDate.set(false);
    this.bulkApplyDateToTicket.set(false);
    this.bulkFinish.set(null);
    this.selectedTaskIds.set(new Set<string>());
    this.loadInitialOptions();
    this.showBulkUpdate.set(true);
  }

  isTaskSelected(taskId: string): boolean {
    return this.selectedTaskIds().has(taskId);
  }

  toggleTaskSelection(taskId: string): void {
    const current = new Set(this.selectedTaskIds());
    if (current.has(taskId)) {
      current.delete(taskId);
    } else {
      current.add(taskId);
    }
    this.selectedTaskIds.set(current);
  }

  toggleSelectAll(): void {
    if (this.allTasksSelected()) {
      this.selectedTaskIds.set(new Set<string>());
    } else {
      this.selectedTaskIds.set(new Set(this.tasks().map(t => t.id)));
    }
  }

  getTaskCardLabel(task: TaskWithProgress): string {
    return task.summary || task.description?.substring(0, 50) || 'Sem descrição';
  }

  closeBulkUpdate(): void {
    this.showBulkUpdate.set(false);
  }

  onBulkRemoveExpectedDateChange(checked: boolean): void {
    this.bulkRemoveExpectedDate.set(checked);
    if (checked) {
      this.bulkExpectedDate.set(null);
      this.bulkApplyDateToTicket.set(false);
    }
  }

  onBulkExpectedDateChange(date: Date | null): void {
    if (!date) {
      this.bulkApplyDateToTicket.set(false);
    }
  }

  confirmBulkUpdate(): void {
    const statusId = this.bulkStatusId();
    const userId = this.bulkUserId();
    const expectedDate = this.bulkExpectedDate();
    const removeExpectedDate = this.bulkRemoveExpectedDate();
    const finish = this.bulkFinish();

    if (this.selectedCount() === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione ao menos uma tarefa para atualizar.',
        life: 3000,
      });
      return;
    }

    if (!statusId && !userId && !expectedDate && !removeExpectedDate && finish === null) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione ao menos uma alteração.',
        life: 3000,
      });
      return;
    }

    const selectedIds = this.selectedTaskIds();
    const payload = this.tasks()
      .filter(task => selectedIds.has(task.id))
      .map(task => {
        const taskPayload: any = { id: task.id };
        if (statusId) taskPayload.ticket_status_id = statusId;
        if (userId) taskPayload.user_id = userId;
        if (finish !== null) taskPayload.finished = finish;
        if (removeExpectedDate) {
          taskPayload.expected_date = null;
        } else if (expectedDate) {
          taskPayload.expected_date = this.toLocalISOString(expectedDate);
        }
        return taskPayload;
      });

    const applyToTicket = !!expectedDate && !removeExpectedDate && this.bulkApplyDateToTicket();

    this.saving.set(true);
    this.ticketService.patchMultipleTasks(this.ticket.id, payload, applyToTicket).subscribe({
      next: updatedTasks => {
        this.tasks.update(currentTasks => {
          const newTasks = [...currentTasks];
          updatedTasks.forEach((updated: TicketTask) => {
            const index = newTasks.findIndex(t => t.id === updated.id);
            if (index > -1) {
              newTasks[index] = { ...newTasks[index], ...updated };
            }
          });
          return newTasks;
        });
        this.saving.set(false);
        this.closeBulkUpdate();
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: `${payload.length} tarefa(s) atualizadas em massa`,
        });
        this.tasksUpdated.emit(this.tasks());
        this.ticketRefreshRequired.emit('status');
      },
      error: () => {
        this.saving.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao atualizar tarefas em massa',
          life: 5000,
        });
      },
    });
  }

  confirmDeleteTask(task: TicketTask): void {
    if (!this.canEdit) return;
    this.confirmationService.confirm({
      message: `Deseja realmente excluir a tarefa "${task.summary || task.description}"?`,
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteTask(task),
    });
  }

  private deleteTask(task: TicketTask): void {
    this.ticketService
      .deleteTask(this.ticket.id, task.id, this.dataService.shouldRecalculateTaskEffort)
      .subscribe({
        next: () => {
          this.tasks.update(currentTasks => currentTasks.filter(t => t.id !== task.id));
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Tarefa excluída',
          });
          this.tasksUpdated.emit(this.tasks());
          this.ticketRefreshRequired.emit('status');
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao excluir tarefa',
            life: 5000,
          });
        },
      });
  }

  toggleTaskFinished(task: TaskWithProgress, checked?: boolean): void {
    if (!this.canEdit) return;

    const newFinished = typeof checked === 'boolean' ? checked : !task.finished;
    if (newFinished && this.getActiveTaskLog(task)) {
      this.tasks.update(currentTasks =>
        currentTasks.map(t => (t.id === task.id ? { ...t, finished: false } : t))
      );
      this.cdr.markForCheck();
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Não é possível concluir a tarefa enquanto houver apontamento em aberto.',
        life: 4000,
      });
      return;
    }

    this.ticketService.updateTask(this.ticket.id, task.id, { finished: newFinished }).subscribe({
      next: updatedTask => {
        this.tasks.update(currentTasks => {
          const index = currentTasks.findIndex(t => t.id === updatedTask.id);
          if (index > -1) {
            const newTasks = [...currentTasks];
            newTasks[index] = {
              ...newTasks[index],
              ...updatedTask,
              summary: updatedTask.summary || '',
            };
            return newTasks;
          }
          return currentTasks;
        });
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Status da tarefa atualizado.',
          life: 3000,
        });
        this.tasksUpdated.emit(this.tasks());
        this.ticketRefreshRequired.emit('status');
      },
      error: () => {
        // Reverte o estado do checkbox para o valor original
        this.tasks.update(currentTasks =>
          currentTasks.map(t => (t.id === task.id ? { ...t, finished: task.finished } : t))
        );
        this.cdr.markForCheck();
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao atualizar tarefa',
          life: 5000,
        });
      },
    });
  }

  onStartTimeEntry(task: TicketTask): void {
    if (this.isTimeEntryBlocked(task)) return;
    this.startTimeEntry.emit(task.id);
  }

  onDragStart(index: number): void {
    if (!this.canEdit) return;
    this.draggedIndex.set(index);
  }

  onDragEnd(): void {
    this.draggedIndex.set(null);
    this.dropTarget.set(null);
  }

  onDragLeave(): void {
    this.dropTarget.set(null);
  }

  onDragOver(event: DragEvent, index: number): void {
    if (!this.canEdit) return;
    event.preventDefault();
    const targetElement = event.currentTarget as HTMLElement;
    const rect = targetElement.getBoundingClientRect();
    const isTopHalf = event.clientY < rect.top + rect.height / 2;
    this.dropTarget.set({ index, position: isTopHalf ? 'top' : 'bottom' });
    this.cdr.markForCheck();
  }

  onDrop(): void {
    if (!this.canEdit) return;
    const sourceIndex = this.draggedIndex();
    const dropTargetInfo = this.dropTarget();

    if (sourceIndex === null || dropTargetInfo === null) {
      this.draggedIndex.set(null);
      this.dropTarget.set(null);
      return;
    }

    let finalTargetIndex = dropTargetInfo.index;
    if (sourceIndex < dropTargetInfo.index && dropTargetInfo.position === 'top') {
      finalTargetIndex--;
    } else if (sourceIndex > dropTargetInfo.index && dropTargetInfo.position === 'bottom') {
      finalTargetIndex++;
    }

    if (sourceIndex === finalTargetIndex) {
      this.draggedIndex.set(null);
      this.dropTarget.set(null);
      return;
    }

    const tasks = [...this.tasks()];
    const [moved] = tasks.splice(sourceIndex, 1);
    tasks.splice(finalTargetIndex, 0, moved);

    const updatedTasksWithSequence = tasks.map((task, index) => ({ ...task, sequence: index + 1 }));

    this.tasks.set(updatedTasksWithSequence);
    this.draggedIndex.set(null);

    const payload = updatedTasksWithSequence.map(t => ({ id: t.id, sequence: t.sequence }));
    this.ticketService.reorderTasks(this.ticket.id, payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Ordem das tarefas salva.',
        });
        this.tasksUpdated.emit(this.tasks());
      },
      error: () => {
        this.loadTasksWithProgress();
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao reordenar tarefas',
          life: 5000,
        });
      },
    });
  }

  isTaskToggleDisabled(task: TicketTask): boolean {
    return !this.canEdit || (!!this.getActiveTaskLog(task) && !task.finished);
  }

  getDifficultyClass(difficulty: string): string {
    switch (difficulty?.toUpperCase()) {
      case 'F':
        return 'difficulty-easy';
      case 'M':
        return 'difficulty-medium';
      case 'D':
        return 'difficulty-hard';
      default:
        return 'difficulty-medium';
    }
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return '—';
    }
  }

  getProgressColor(percentage: number): string {
    if (percentage >= 100) return '#10b981';
    if (percentage >= 50) return '#3b82f6';
    if (percentage > 0) return '#f59e0b';
    return '#94a3b8';
  }

  getStatusColor(desc: string | undefined): string {
    if (!desc) return 'var(--p-surface-500, #64748b)';
    const d = desc.toLowerCase();

    // Testes específicos e aprovações (Verde)
    if (d.includes('teste ok') || d.includes('homologação')) return 'var(--p-green-500, #22c55e)';

    // Testes falhas e bloqueios (Vermelho)
    if (
      d.includes('teste não ok') ||
      d.includes('teste nao ok') ||
      d.includes('reprovado') ||
      d.includes('bloqueado')
    )
      return 'var(--p-red-500, #ef4444)';

    // Outros testes parciais, aguardando (Amarelo)
    if (d.includes('testar') || d.includes('teste')) return 'var(--p-yellow-400, #facc15)';

    // Status de trabalho ou revisão (Azul)
    if (d.includes('aguardando desenvolvimento')) return 'var(--p-surface-500, #64748b)';
    if (d.includes('andamento') || d.includes('desenvolvimento') || d.includes('revisão'))
      return 'var(--p-blue-500, #3b82f6)';

    // Neutros
    if (d.includes('esforço definido')) return 'var(--p-surface-700, #334155)';
    return 'var(--p-surface-500, #64748b)'; // Default fallback for unknown statuses
  }

  getTaskTimeEntryIcon(task: TicketTask): string {
    const activeLog = this.getActiveTaskLog(task);
    if (!activeLog) return 'pi-play';

    const isCurrentUser = this.isCurrentUserLoggingTask(task);
    return isCurrentUser ? 'pi-stop' : 'pi-play';
  }

  getTaskTimeEntryTooltip(task: TicketTask): string {
    if (this.isTimeEntryBlocked(task)) {
      return 'Encerre o apontamento anterior para iniciar.';
    }

    const activeLog = this.getActiveTaskLog(task);
    if (!activeLog) return 'Iniciar Apontamento';

    const isCurrentUser = this.isCurrentUserLoggingTask(task);
    if (isCurrentUser) {
      return 'Encerrar Apontamento';
    } else {
      return `Iniciar Acompanhamento (${activeLog.user?.name || 'Usuário'} apontando)`;
    }
  }

  /**
   * O usuário já tem apontamento ativo neste chamado em outra tarefa — só é permitido um por
   * chamado, então o play das demais tarefas fica bloqueado.
   */
  isTimeEntryBlocked(task: TicketTask): boolean {
    return isTaskTimeEntryBlocked(this.ticket?.ticket_logs, task, getCurrentUserId());
  }

  private isCurrentUserLoggingTask(task: TicketTask): boolean {
    const activeLog = this.getActiveTaskLog(task);
    if (!activeLog) return false;

    const currentUserId = getCurrentUserId();
    return activeLog.user_id === currentUserId || activeLog.user?.id === currentUserId;
  }

  hasOpenTimeEntry(): boolean {
    const editing = this.editingTask();
    return !!editing && !!this.getActiveTaskLog(editing);
  }

  isFinishedDisabled(): boolean {
    return this.hasOpenTimeEntry() && !this.formFinished();
  }

  getActiveTaskLog(task: TicketTask) {
    return findActiveTaskLog(this.ticket?.ticket_logs, task);
  }

  private toLocalISOString(date: Date): string {
    const offset = -date.getTimezoneOffset();
    const offsetSign = offset >= 0 ? '+' : '-';
    const offsetHours = Math.floor(Math.abs(offset) / 60)
      .toString()
      .padStart(2, '0');
    const offsetMinutes = (Math.abs(offset) % 60).toString().padStart(2, '0');

    return (
      date.getFullYear() +
      '-' +
      (date.getMonth() + 1).toString().padStart(2, '0') +
      '-' +
      date.getDate().toString().padStart(2, '0') +
      'T' +
      date.getHours().toString().padStart(2, '0') +
      ':' +
      date.getMinutes().toString().padStart(2, '0') +
      ':' +
      date.getSeconds().toString().padStart(2, '0') +
      offsetSign +
      offsetHours +
      ':' +
      offsetMinutes
    );
  }
}
